import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
  Alert,
  CircularProgress,
  Typography
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { forgotPasswordAsync, clearError, clearResetMessage } from "../../../store/slices/authSlice";

/**
 * Formulário para solicitar reset de senha.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Se o modal está aberto
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
function ForgotPasswordForm({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();
  const { isLoading, error, resetMessage } = useSelector((state) => state.auth);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  useEffect(() => {
    if (open) {
      dispatch(clearError());
      dispatch(clearResetMessage());
    }
  }, [open, dispatch]);

  const validateEmail = (email) => {
    if (!email) return 'Email é obrigatório';
    if (!/\S+@\S+\.\S+/.test(email)) return 'Email inválido';
    return '';
  };

  const handleChange = (e) => {
    setEmail(e.target.value);
    if (emailError) {
      setEmailError('');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const error = validateEmail(email);
    if (error) {
      setEmailError(error);
      return;
    }
    dispatch(forgotPasswordAsync(email));
  };

  const handleClose = () => {
    setEmail('');
    setEmailError('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullScreen={isMobile}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Esqueci minha senha
        <IconButton onClick={handleClose} size="small">
          <CloseOutlinedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {resetMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {resetMessage}
          </Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Digite seu email para receber as instruções de reset de senha.
        </Typography>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            name="email"
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={handleChange}
            error={!!emailError}
            helperText={emailError}
            disabled={isLoading || !!resetMessage}
          />
          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button 
              type="submit" 
              variant="contained" 
              fullWidth
              disabled={isLoading || !!resetMessage}
              startIcon={isLoading && <CircularProgress size={20} />}
            >
              {isLoading ? 'Enviando...' : 'Enviar'}
            </Button>
            <Button onClick={handleClose} variant="outlined" fullWidth>
              {resetMessage ? 'Fechar' : 'Cancelar'}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default ForgotPasswordForm;